'use client';

import { useState } from 'react';
import { Crown, Check, Sparkles, Zap, Download, FileText, GraduationCap, BarChart3, X, Calculator, GitCompare, Settings, RefreshCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePremium, PRICING } from '@/contexts/PremiumContext';
import { toast } from 'react-hot-toast';
import { useSession } from 'next-auth/react';
import { useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';

export default function PremiumPage() {
  const { isPremium, currentPeriodEnd, cancelAtPeriodEnd, refreshSubscription } = usePremium();
  const { data: session } = useSession();
  const router = useRouter();
  const t = useTranslations('Premium');
  const locale = useLocale();

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isRefundLoading, setIsRefundLoading] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);

  const handleManageSubscription = async () => {
    setIsPortalLoading(true);
    try {
      const res = await fetch('/api/stripe/create-portal-session', { method: 'POST' });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch {
      toast.error(t('errorPortal'));
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleRefund = async () => {
    setIsRefundLoading(true);
    try {
      const res = await fetch('/api/stripe/refund', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || t('errorRefund'));
        return;
      }
      toast.success(t('successRefund'), { duration: 6000 });
      setShowRefundConfirm(false);
      await refreshSubscription();
    } catch {
      toast.error(t('errorRefundContact'));
    } finally {
      setIsRefundLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!session?.user) {
      router.push('/auth/signin');
      return;
    }

    setIsLoading(true);
    try {
      const priceId = selectedPlan === 'monthly' ? PRICING.monthly.priceId : PRICING.yearly.priceId;

      const res = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId }),
      });

      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch {
      toast.error(t('errorPayment'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isPremium) {
    const periodEndDate = currentPeriodEnd
      ? new Date(currentPeriodEnd).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

    return (
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 border-2 border-[#FFD700] rounded-full mb-8">
            <Crown className="w-6 h-6 text-[#FFD700]" />
            <span className="text-xl font-bold text-[#FFD700]">{t('activeMember')}</span>
            <Sparkles className="w-6 h-6 text-[#FFD700]" />
          </div>

          <h1 className="text-4xl font-bold text-white mb-4">{t('thankYou')}</h1>
          <p className="text-xl text-[#a8b2d1] mb-8">{t('thankYouDesc')}</p>

          <div className="max-w-xl mx-auto mb-12 p-6 bg-[#112240] border border-[#233554] rounded-xl text-left space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#64ffda]" />
              {t('manageTitle')}
            </h2>

            {periodEndDate && (
              <p className="text-sm text-[#a8b2d1]">
                {cancelAtPeriodEnd
                  ? <>{t('cancelsOn', { date: periodEndDate })}</>
                  : <>{t('renewsOn', { date: periodEndDate })}</>
                }
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleManageSubscription}
                disabled={isPortalLoading}
                className="bg-[#233554] hover:bg-[#2d4a73] text-white border border-[#64ffda]/30 flex-1"
              >
                <Settings className="w-4 h-4 mr-2" />
                {isPortalLoading ? t('loading') : t('billingPortal')}
              </Button>

              {!showRefundConfirm ? (
                <Button
                  onClick={() => setShowRefundConfirm(true)}
                  variant="outline"
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 flex-1"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  {t('guarantee30')}
                </Button>
              ) : (
                <div className="flex-1 p-4 bg-red-500/10 border border-red-500/40 rounded-lg space-y-3">
                  <p className="text-sm text-red-300 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {t('refundConfirmText')}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleRefund}
                      disabled={isRefundLoading}
                      size="sm"
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      {isRefundLoading ? t('processing') : t('confirm')}
                    </Button>
                    <Button
                      onClick={() => setShowRefundConfirm(false)}
                      size="sm"
                      variant="outline"
                      className="border-[#233554] text-[#a8b2d1]"
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-[#a8b2d1]">{t('guaranteeNote')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {[
              { icon: <Zap className="w-8 h-8" />, titleKey: 'feature3dTitle' as const, descKey: 'featureDesc3d' as const },
              { icon: <X className="w-8 h-8" />, titleKey: 'featureNoAdsTitle' as const, descKey: 'featureDescNoAds' as const },
              { icon: <GitCompare className="w-8 h-8" />, titleKey: 'featureCompareTitle' as const, descKey: 'featureDescCompare' as const },
              { icon: <Calculator className="w-8 h-8" />, titleKey: 'featureCalculatorTitle' as const, descKey: 'featureDescCalculator' as const },
              { icon: <GraduationCap className="w-8 h-8" />, titleKey: 'featureQuizTitle' as const, descKey: 'featureDescQuiz' as const },
              { icon: <BarChart3 className="w-8 h-8" />, titleKey: 'featureRevisionTitle' as const, descKey: 'featureDescRevision' as const },
              { icon: <FileText className="w-8 h-8" />, titleKey: 'featurePdfTitle' as const, descKey: 'featureDescPdf' as const },
              { icon: <Download className="w-8 h-8" />, titleKey: 'featureCsvTitle' as const, descKey: 'featureDescCsv' as const },
            ].map((f, i) => (
              <PremiumFeatureCard key={i} icon={f.icon} title={t(f.titleKey)} description={t(f.descKey)} active />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 border border-[#FFD700]/30 rounded-full mb-6">
          <Crown className="w-5 h-5 text-[#FFD700]" />
          <span className="text-sm font-semibold text-[#FFD700]">Premium</span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          {t('heroTitle1')}
          <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
            {t('heroTitle2')}
          </span>
        </h1>

        <p className="text-xl text-[#a8b2d1] max-w-3xl mx-auto mb-8">
          {t('heroSubtitle')}
        </p>
      </div>

      <div className="max-w-5xl mx-auto mb-16">
        <div className="grid md:grid-cols-2 gap-8">
          <PricingCard
            title={t('monthly')}
            price={PRICING.monthly.price}
            currency={PRICING.monthly.currency}
            period={PRICING.monthly.period}
            isSelected={selectedPlan === 'monthly'}
            onSelect={() => setSelectedPlan('monthly')}
          />
          <PricingCard
            title={t('yearly')}
            price={PRICING.yearly.price}
            currency={PRICING.yearly.currency}
            period={PRICING.yearly.period}
            savings={t('save', { savings: PRICING.yearly.savings })}
            recommendedLabel={t('recommended')}
            recommended
            isSelected={selectedPlan === 'yearly'}
            onSelect={() => setSelectedPlan('yearly')}
          />
        </div>

        <div className="text-center mt-8">
          <Button
            size="lg"
            onClick={handleUpgrade}
            className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90 font-bold text-lg px-12 py-6 rounded-xl"
            disabled={isLoading}
          >
            <Crown className="w-6 h-6 mr-2" />
            {isLoading
              ? t('loading')
              : t('upgradeTo', {
                  price: selectedPlan === 'monthly' ? PRICING.monthly.price : PRICING.yearly.price,
                  currency: PRICING.monthly.currency,
                })}
          </Button>
          <p className="text-sm text-[#a8b2d1] mt-4">{t('cancelAnytime')}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto mb-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">{t('whatYouGet')}</h2>

        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard
            icon={<Zap className="w-6 h-6" />}
            title={t('feature3dTitle')}
            description={t('feature3dDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={t('feature3dFree')}
            premium={t('feature3dPremium')}
          />
          <FeatureCard
            icon={<X className="w-6 h-6" />}
            title={t('featureNoAdsTitle')}
            description={t('featureNoAdsDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={t('featureNoAdsFree')}
            premium={t('featureNoAdsPremium')}
          />
          <FeatureCard
            icon={<GitCompare className="w-6 h-6" />}
            title={t('featureCompareTitle')}
            description={t('featureCompareDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimitedAccess')}
          />
          <FeatureCard
            icon={<Calculator className="w-6 h-6" />}
            title={t('featureCalculatorTitle')}
            description={t('featureCalculatorDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimitedAccess')}
          />
          <FeatureCard
            icon={<GraduationCap className="w-6 h-6" />}
            title={t('featureQuizTitle')}
            description={t('featureQuizDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimitedAccess')}
          />
          <FeatureCard
            icon={<BarChart3 className="w-6 h-6" />}
            title={t('featureRevisionTitle')}
            description={t('featureRevisionDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimitedAccess')}
          />
          <FeatureCard
            icon={<FileText className="w-6 h-6" />}
            title={t('featurePdfTitle')}
            description={t('featurePdfDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimited')}
          />
          <FeatureCard
            icon={<Download className="w-6 h-6" />}
            title={t('featureCsvTitle')}
            description={t('featureCsvDesc')}
            freeLabel={t('free')}
            premiumLabel={t('premiumLabel')}
            free={null}
            premium={t('unlimited')}
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-white text-center mb-12">{t('faqTitle')}</h2>
        <div className="space-y-6">
          <FAQItem question={t('faq1Q')} answer={t('faq1A')} />
          <FAQItem question={t('faq2Q')} answer={t('faq2A')} />
          <FAQItem question={t('faq3Q')} answer={t('faq3A')} />
          <FAQItem question={t('faq4Q')} answer={t('faq4A')} />
        </div>
      </div>

      <div className="text-center mt-16 p-12 bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 border-2 border-[#FFD700]/30 rounded-2xl">
        <Crown className="w-16 h-16 text-[#FFD700] mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-white mb-4">{t('ctaTitle')}</h2>
        <p className="text-xl text-[#a8b2d1] mb-8 max-w-2xl mx-auto">{t('ctaSubtitle')}</p>
        <Button
          size="lg"
          onClick={handleUpgrade}
          className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90 font-bold text-lg px-12 py-6 rounded-xl"
          disabled={isLoading}
        >
          <Crown className="w-6 h-6 mr-2" />
          {isLoading ? t('loading') : t('startNow')}
        </Button>
      </div>
    </div>
  );
}

function PricingCard({
  title,
  price,
  currency,
  period,
  savings,
  recommendedLabel,
  recommended = false,
  isSelected,
  onSelect,
}: {
  title: string;
  price: number;
  currency: string;
  period: string;
  savings?: string;
  recommendedLabel?: string;
  recommended?: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`relative p-8 rounded-2xl border-2 transition-all cursor-pointer ${
        isSelected
          ? 'border-[#FFD700] bg-gradient-to-br from-[#FFD700]/10 to-[#FFA500]/10 scale-105'
          : 'border-[#233554] bg-[#112240] hover:border-[#FFD700]/50'
      }`}
    >
      {recommended && recommendedLabel && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-full">
            <span className="text-sm font-bold text-[#0a192f]">{recommendedLabel}</span>
          </div>
        </div>
      )}

      {savings && (
        <div className="absolute -top-4 right-4">
          <div className="px-3 py-1 bg-green-500 rounded-full">
            <span className="text-xs font-bold text-white">{savings}</span>
          </div>
        </div>
      )}

      <div className="text-center">
        <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
        <div className="mb-6">
          <span className="text-5xl font-bold text-[#FFD700]">{price}{currency}</span>
          <span className="text-[#a8b2d1]">/{period}</span>
        </div>
        <div className="w-12 h-12 mx-auto rounded-full border-2 flex items-center justify-center mb-4">
          {isSelected && <div className="w-6 h-6 rounded-full bg-[#FFD700]"></div>}
        </div>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  freeLabel,
  premiumLabel,
  free,
  premium,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  freeLabel: string;
  premiumLabel: string;
  free: string | null;
  premium: string;
}) {
  return (
    <div className="p-6 bg-[#112240] border border-[#233554] rounded-xl hover:border-[#64ffda]/30 transition-all">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gradient-to-br from-[#64ffda]/20 to-[#82aaff]/20 rounded-lg text-[#64ffda]">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
          <p className="text-sm text-[#a8b2d1] mb-4">{description}</p>
          <div className="flex items-center gap-8 text-sm">
            {free && (
              <div>
                <span className="text-[#a8b2d1]">{freeLabel} </span>
                <span className="text-white">{free}</span>
              </div>
            )}
            <div>
              <span className="text-[#FFD700]">{premiumLabel} </span>
              <span className="text-white font-semibold">{premium}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PremiumFeatureCard({
  icon,
  title,
  description,
  active,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  active: boolean;
}) {
  return (
    <div className="p-6 bg-[#112240] border-2 border-[#FFD700]/30 rounded-xl">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gradient-to-br from-[#FFD700]/20 to-[#FFA500]/20 rounded-lg text-[#FFD700]">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {active && <Check className="w-5 h-5 text-green-500" />}
          </div>
          <p className="text-sm text-[#a8b2d1]">{description}</p>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      onClick={() => setIsOpen(!isOpen)}
      className="p-6 bg-[#112240] border border-[#233554] rounded-xl cursor-pointer hover:border-[#64ffda]/30 transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-lg font-semibold text-white">{question}</h3>
        <div className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <svg className="w-6 h-6 text-[#64ffda]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isOpen && <p className="mt-4 text-[#a8b2d1] leading-relaxed">{answer}</p>}
    </div>
  );
}
